import React, { useState, useMemo, useEffect } from 'react';
import { Box, CircularProgress, Divider, Tabs, Typography } from '@mui/material';
import AssignedReviewTeam from './ReviewTeam/AssignedReviewTeam';
import CollapsibleAssignedReviewTeam from './ReviewTeam/CollapsibleAssignedReviewTeam';
import SepHeader from './SepHeader';
import Tab from '../../components/Tabs';
import VerticalTabs from '../../components/VerticalTabs';
import { useAccount } from '@azure/msal-react';
import { useGetDepartmentsQuery, useGetDepartmentsExtendedQuery } from '../../services/departmentSlice/departmentSlice';
import AttachmentUpload from '../../components/AttachmentUpload/AttachmentUpload';
import Comments from '../../components/Comments/Comments';
import Activities from '../../components/Activity/Activities';
import { useGetSepTasksQuery, useGetSepQuery } from '../../services/sepSlice';
import { useGetDataFieldsBySEPIDQuery } from '../../services/dataFieldsSlice';
import { useGetAttachmentByIDQuery } from '../../services/attachmentSlice/attachmentSlice';
import { useGetCommentsQuery } from '../../services/commentSlice/commentSlice';
import { useGetUserQuery, useGetUserSettingTasksQuery } from '../../services/usersSlice';
import { env } from '../../../shared/env';

// types
import { DataFieldWithOptionsAndLocations } from '../../../shared/types/DataField';
import { useUpdateDataFieldsMutation } from '../../services/dataFieldsSlice';
import { TaskExtended } from '../../../shared/types/Task';
import { Department } from '../../../shared/types/Department';
import useMapData from '../../hooks/useMapData';
import { SEPPhase, SEPStatus } from '../../../shared/types/SEP';
import KnockoutDetails from './KnockoutDetails';
import { TaskList } from './TaskList';
import DepartmentIconRenderer from '../../assets/svg/departmentIconMap';

//import { CommentExtended } from '../../../shared/types/Comment';

interface SepOverviewProps {
  sepID: number;
  onNewTask: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TabProperties {
  label: string;
  id?: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SepOverview = ({ sepID, onNewTask }: SepOverviewProps) => {
  const account = useAccount();
  const isAdminUser = env.REACT_APP_LOCAL_DEV_ADMIN === 'true' || (account?.idTokenClaims?.roles?.includes('AuthSuperUser') ?? false);
  const { data: loggedInUser, isLoading } = useGetUserQuery('me');
  const [updateDataFields] = useUpdateDataFieldsMutation();
  const [filterTasks, setFilterTasks] = useState(false);
  const [hidePendingTasks, setHidePendingTasks] = useState(false);
  const [hidePendingOtherTasks, setHidePendingOtherTasks] = useState(false);
  const [hidePendingDeptTasks, setHidePendingDeptTasks] = useState(true);
  const [hideOthers, setHideOthers] = useState(true);
  const [filterOthers, setFilterOthers] = useState(false);

  const [currentTab, setCurrentTab] = useState(0);
  const { data: departments } = useGetDepartmentsQuery();
  // Pull full task list since sep.tasks only contains enabled tasks
  const { data: tasks, refetch, isLoading: isTaskListLoading } = useGetSepTasksQuery(sepID);
  const [currentSubTab, setcurrentSubTab] = useState('DepartmentReview');
  const [dataChanged, setDataChanged] = useState(false);
  const sepData = useGetSepQuery(sepID);
  const isComplete = sepData?.data?.phase === SEPPhase.complete;
  const disableAttachmentDelete = sepData.data && [
    SEPStatus.approved,
    SEPStatus.cancelled,
    SEPStatus.rejected,
    SEPStatus.expired,
  ].includes(sepData.data.status);
  const creatorId = sepData.data?.creator.id;
  const { data: getTaskSetting } = useGetUserSettingTasksQuery();


  

  const roleList: any = useMemo(() => {
    if (env.REACT_APP_LOCAL_DEV_ADMIN === 'true') {
      return ['AuthSuperUser'];
    } else {
      return account?.idTokenClaims?.roles || [];
    }
  }, [account]);

  const { data: dataFields, refetch: rerun } =
    useGetDataFieldsBySEPIDQuery(sepID);
  const { data: attachments } = useGetAttachmentByIDQuery(sepID);
  const { data: comments } = useGetCommentsQuery(sepID);
  const { data: departmentOrder } = useGetDepartmentsExtendedQuery();

  const handleHide = () => {
    setHideOthers(!hideOthers);
  };

  const handleTasksSwitch = () => {
    setFilterTasks(!filterTasks);
  };

  const handleCompletedSwitch = () => {
    setFilterOthers(!filterOthers);
  };

  //Auth user roles with departments
  const userTabs = useMemo(() => {
    let tabList: TabProperties[] = [{ label: 'Task List' }];
    if (roleList && roleList.length && departments) {
      const superUser = roleList?.includes('AuthSuperUser');
      departments.forEach((department: Department) => {
        const adRole = roleList?.find(
          (role: any) => role === department.adAppRole
        );
        if (adRole || superUser) {
          tabList.push({ label: department.name, id: department.id });
        }
      });
    }
    tabList = tabList.concat([
      { label: 'Attachments' },
      { label: 'Comments' },
      { label: 'Activity' },
    ]);
    return tabList;
  }, [roleList, departments]);

  const tabName = userTabs[currentTab].label;
  const standardTabNames = ['Task List', 'Attachments', 'Comments', 'Activity'];
  const currentDepartmentID = userTabs[currentTab].id;

  const dataFieldsForDepartment = useMemo(() => {
    const newDataFields: DataFieldWithOptionsAndLocations[] = [];
    if (currentDepartmentID) {
      dataFields?.forEach((field) => {
        const isPresent = field.dataFieldLocations.find(
          (location) =>
            location.locationType === currentSubTab &&
            location.locationID === currentDepartmentID
        );
        if (isPresent) {
          newDataFields.push(field);
        }
      });
    }
    return newDataFields;
  }, [currentSubTab, currentDepartmentID, dataFields]);

  let sortedFields: any[];

  function getReorderedFieldsForDepartment(
    departmentId: number,
    departmentOrder: any,
    datafields: any[]
  ) {
    const department = departmentOrder?.find(
      (field: any) => field.id == departmentId
    );
    const validDataFieldIds = new Set(
      datafields?.map((df) => df.dataFieldTemplateID)
    );
    return department?.departmentDataFields?.filter((field: any) =>
      validDataFieldIds.has(field.id)
    );
  }

  function mergeAndOrderDataFields(
    datafields: any[],
    reOrderDataFields: any[]
  ) {
    const reOrderedMap = new Map(
      reOrderDataFields?.map((field: any) => [field.id, field])
    );
    return datafields?.map((field) => {
      const matchedField = reOrderedMap?.get(field.dataFieldTemplateID);
      return {
        ...field,
        order: matchedField?.dataFieldLocation?.order,
      };
    });
  }

  function sortByOrder(fields: any[]) {
    return fields.slice().sort((a, b) => a.order - b.order);
  }

  if (currentDepartmentID) {
    const reOrderedFields = getReorderedFieldsForDepartment(
      currentDepartmentID,
      departmentOrder,
      dataFieldsForDepartment
    );
    const mergerdFields = mergeAndOrderDataFields(
      dataFieldsForDepartment,
      reOrderedFields
    );
    sortedFields = sortByOrder(mergerdFields);
  }
  const tasksForDepartment = useMemo(() => {
    const currentTasks: TaskExtended[] = [];
    tasks?.forEach((task: any) => {
      if (currentDepartmentID === task.departmentID) {
        currentTasks.push(task);
      }
    });
    return currentTasks;
  }, [currentDepartmentID, tasks]);

  const { data, setData, buttonValidation } = useMapData({
    dataFieldsForDepartment,
    currentDepartmentID,
    currentSubTab,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setData([]);
    setDataChanged(false);
    setCurrentTab(newValue);
  };

  const handleSubTabChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => {
    setData([]);
    setcurrentSubTab(newValue);
  };

  const handleCancel = () => {
    setDataChanged(false);
    setData(dataFieldsForDepartment);
  };

  const handleSave = async () => {
    await updateDataFields({ sepId: sepID, dataFields: data });
    rerun();
  };

let settings: any = {}
  useEffect(()=> {
    if (loggedInUser?.id && !isLoading && getTaskSetting && getTaskSetting.userProfileSettings) {
        settings = JSON.parse(getTaskSetting.userProfileSettings);
        const {hideMyPendingTasks, hideMyCompleteTasks, hideOtherPendingTasks, hideOtherCompleteTasks} = settings
        setHidePendingTasks(creatorId === loggedInUser.id ? true : hideMyPendingTasks)
        setHidePendingOtherTasks(hideOtherPendingTasks)
        setFilterTasks(hideMyCompleteTasks)
        setFilterOthers(hideOtherCompleteTasks)
      setHidePendingDeptTasks(false)
    }
  },[creatorId, loggedInUser, isLoading, getTaskSetting])

  const standartTabSelector = (tabName: string): JSX.Element => {
    switch (tabName) {
      case 'Task List':
        if (isTaskListLoading) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my:4, py: 4 }}>
              <CircularProgress />
            </Box>
          );
        }

        return (
          <TaskList
            sepID={sepID}
            filterTasks={filterTasks}
            filterOthers={filterOthers}
            hideOthers={hideOthers}
            handleHide={handleHide}
            handleTasksSwitch={handleTasksSwitch}
            handleCompletedSwitch={handleCompletedSwitch}
            onNewTaskAdded={(newTask: TaskExtended) => {
              onNewTask();
              refetch();
            }}
            hideFilterChange={() => setHidePendingTasks(!hidePendingTasks)}
            hidePendingTasks={hidePendingTasks}
            hidePendingOtherTasks={hidePendingOtherTasks}
            hideFilterChangeOthers={() => setHidePendingOtherTasks(!hidePendingOtherTasks)}
          />
        );
      case 'Comments':
        return (
          <Comments
            sepID={sepID}
            comments={comments || []}
            roleList={roleList}
          />
        );

      case 'Activity':
        return <Activities sepID={sepID} />;
      default:
        return (
          <AttachmentUpload attachments={attachments || []} sepID={sepID} isAdminUser={isAdminUser} isComplete={isComplete} disableDelete={disableAttachmentDelete}/>
        );
    }
  };

  return (
    <Box sx={{ m: 0, p: 0 }}>
      <SepHeader dataFields={dataFields} sepID={sepID} roleList={roleList} />
      {/* <Divider /> */}
      <KnockoutDetails sepID={sepID} />
      <Divider />
      <CollapsibleAssignedReviewTeam sepID={sepID} />
      {/* <Divider /> */}

      {/* Middle Desc */}
      <Box sx={{ pr: { xs: 1, sm: 2, md: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs
          scrollButtons
          value={currentTab}
          TabIndicatorProps={{ style: { backgroundColor: 'white' } }}
          variant="scrollable"
          onChange={handleTabChange}
        >
          {userTabs.map((link, index) => (
            <Tab 
              key={index} 
              id={link.id} 
              label={link.label}
              icon={<DepartmentIconRenderer departmentName={link.label} height={20} width={20} />}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>
      {/* <Divider /> */}

      {!standardTabNames.includes(tabName)
        ? userTabs.map((link, index) => (
            <TabPanel key={index} value={currentTab} index={index}>
              <VerticalTabs
                currentSubTab={currentSubTab}
                dataFieldsForDepartment={dataFieldsForDepartment}
                handleSubTabChange={handleSubTabChange}
                data={data}
                setData={setData}
                handleCancel={handleCancel}
                handleSave={handleSave}
                tasksForDepartment={tasksForDepartment}
                sepID={sepID}
                filterTasks={filterTasks}
                hidePendingDeptTasks={hidePendingDeptTasks}
                hideFilterChange={() => setHidePendingDeptTasks(!hidePendingDeptTasks)}
                handleTasksSwitch={handleTasksSwitch}
                setDataChanged={setDataChanged}
                dataChanged={dataChanged}
                buttonValidation={buttonValidation}
                currentDepartmentID={currentDepartmentID}
                onNewTaskAdded={() => {
                  onNewTask();
                  refetch();
                }}
                sortData={sortedFields}
                refetchTasks={refetch}
                allSepTasks={tasks}
                isAdminUser={isAdminUser}
              />
            </TabPanel>
          ))
        : standartTabSelector(tabName)}
        
    </Box>
  );
};

export default SepOverview;
